import { handleActions } from 'redux-actions';
import objectAssign from 'object-assign';
import mergeOptions from 'merge-options';
import prodInitial from './order_prod_initial';

const initialState = process.env.NODE_ENV === 'development' ? {} : prodInitial;

const handlerServices = (state, action) => {
  const newState = mergeOptions({},
    state);
  const lineItemIndex = newState.bookingPrice.lineItems
    ? newState.bookingPrice.lineItems.findIndex(
      lineItem => lineItem.serviceId === action.payload.serviceId
    )
    : -1;
  const serviceIndex = newState.services.findIndex(
    service => service.serviceId === action.payload.serviceId
  );
  const updatedLineItem = mergeOptions(
    {},
    newState.services.find(
      service => service.serviceId === action.payload.serviceId
    ),
    { unitCount: action.payload.value }
  );

  // API calculate real lineItem price with some additional rules and put it in LineItem only,
  // when extraServices section does not have this property. However it is required for proper rendering of
  // lineItems section.
  if (!updatedLineItem.price || !updatedLineItem.price.amount) {
    updatedLineItem.price = {
      currencyCode: updatedLineItem.unitPrice.currencyCode,
    };
    if (
      updatedLineItem.unitPrice.amount * updatedLineItem.unitCount >=
      updatedLineItem.minimumPrice.amount
    ) {
      updatedLineItem.price.amount =
        updatedLineItem.unitPrice.amount * updatedLineItem.unitCount;
    } else {
      updatedLineItem.price.amount = updatedLineItem.minimumPrice.amount;
    }
  }

  if (lineItemIndex === -1 && action.payload.value !== 0) {
    newState.bookingPrice.lineItems.push(updatedLineItem);

    newState.bookingPrice.lineItems.sort((a, b) => {
      const i = newState.services.findIndex(
        service => service.serviceId === a.serviceId
      );
      const j = newState.services.findIndex(
        service => service.serviceId === b.serviceId
      );
      return i - j;
    });
  } else if (action.payload.value !== 0) {
    newState.bookingPrice.lineItems[lineItemIndex] = updatedLineItem;
  } else if (
    lineItemIndex !== -1 &&
    newState.bookingPrice.lineItems[lineItemIndex].serviceId === action.payload.serviceId
  ) {
    newState.bookingPrice.lineItems.splice(lineItemIndex,
      1);
  }
  newState.services[serviceIndex].unitCount = action.payload.value;
  return newState;
};

const orderAppState = handleActions(
  {
    INIT_FROM_DRUPAL: state => {
      const globalOrder = window.globalOrder || undefined;
      if (globalOrder instanceof Object) {
        return mergeOptions({}, state, globalOrder);
      }
      return state;
    },

    RESET_STATE: (state, action) =>
      mergeOptions({},
        state,
        action.payload.orderAppState),

    UPDATE_FROM_BACKEND: (state, action) =>
      mergeOptions({},
        state,
        action.payload),

    UPDATE_CUSTOMER_INFO: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.customer[action.payload.propertyName] = action.payload.value;
      return newState;
    },

    UPDATE_CUSTOMER_DIALCODE: (state, action) => {
      const newState = mergeOptions({},
        state);
      const customerPhone = action.payload.propertyName.replace('DialCode',
        '');
      if (state.customer[customerPhone] === '') {
        newState.customer[action.payload.propertyName] = action.payload.value;
      } else {
        newState.customer[action.payload.propertyName] = action.payload.value;
        if (state.customer[customerPhone].indexOf('+') === -1) {
          newState.customer[customerPhone] =
            action.payload.value + state.customer[customerPhone];
        } else {
          newState.customer[customerPhone] =
            action.payload.value +
            state.customer[customerPhone].replace(
              state.customer[action.payload.propertyName],
              ''
            );
        }
      }
      return newState;
    },

    UPDATE_CUSTOMER_PHONE: (state, action) => {
      const newState = mergeOptions({},
        state);
      if (action.payload.value !== '') {
        newState.customer[action.payload.propertyName] =
          state.customer[`${action.payload.propertyName}DialCode`] +
          action.payload.value.replace(/[-/\s]/g,
            '');
      } else {
        newState.customer[action.payload.propertyName] = action.payload.value;
      }
      return newState;
    },

    UPDATE_GUEST_OPTIONS: (state, action) =>
      objectAssign({},
        state,
        {
          [action.payload.propertyName]: action.payload.value,
        }),

    UPDATE_ADDITIONAL_CHILDREN: (state, action) => ({
      ...state,
      childrenFreeOfCharge: action.payload,
    }),

    SELECT_PAYMENT_OPTION: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.payment.paymentOptionSelected = action.payload;

      const selected = newState.payment.paymentOptions.filter(
        option =>
          newState.payment.paymentOptionSelected === option.creditCardExternalId
      );

      if (selected.length > 0 && selected[0].voucherEnabled === false) {
        newState.payment.voucher.amount = 0;
        newState.payment.voucher.code = '';
        newState.payment.voucher.type = '';
        newState.payment.voucher.agencyRef = '';
        newState.payment.voucher.campaignName = '';
      }

      return newState;
    },

    SELECT_PAYMENT_TYPE: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.payment.singleRate = action.payload;
      return newState;
    },

    UPDATE_SALES_CHANNEL: (state, action) => {
      const newState = objectAssign({},
        state);
      newState.salesChannel = action.payload;
      return newState;
    },

    UPDATE_AGENCY_NUMBER: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.agency.agencyNumber = action.payload;
      return newState;
    },

    UPDATE_AGENCY_MESSAGE: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.agency.message = action.payload;
      return newState;
    },

    UPDATE_AGENCY_SUB_ID: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.agency.subId = action.payload;
      return newState;
    },

    UPDATE_ORDER_TYPE: (state, action) =>
      objectAssign({},
        state,
        { type: action.payload }),

    UPDATE_AGENCY_USER_ID: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.agency.userId = action.payload;
      return newState;
    },

    UPDATE_VOUCHER_CODE: (state, action) => {
      const newState = mergeOptions({},
        state);
      newState.payment.voucher.code = action.payload;
      return newState;
    },

    UPDATE_EXTRA_SERVICES: handlerServices,
    UPDATE_TICKETS: handlerServices,

    ADD_SECURITY_PACKAGE_LINK: state => {
      const additionalSecurityPackageLink =
        window.novasolData && window.novasolData.additionalSecurityPackageLink;
      if (!additionalSecurityPackageLink) {
        return state;
      }

      const newState = mergeOptions({},
        state);

      const hasService = (srvID) => newState.services.findIndex(
        service => service.serviceId === srvID
      );
      const setLink = (srvIndex) => {
        newState.services[
          srvIndex
        ].moreInfoLink = additionalSecurityPackageLink;
      };

      // Standard Security Package
      let serviceIndex = hasService('1001');
      if (serviceIndex !== -1) {
        setLink(serviceIndex);
      }

      // SicherheitspaketPlus Security Package
      serviceIndex = hasService('1102');
      if (serviceIndex !== -1) {
        setLink(serviceIndex);
      }

      return newState;
    },
  },
  initialState
);

export default orderAppState;
